// A short message at the foot of the page that hides itself (design-system
// `sm-toast`, _toast.scss).
//
// It takes a line of text and shows it; it knows nothing about what happened or
// where it was triggered from.
//
// Usage from JS:
//   import showToast from 'src/shared/toast/toast';
//   showToast('Saved as "On-market listings"');
//   showToast('AI Outbound Voice could not be turned on.', { variant: 'error' });
//
// Usage from a server-rendered page (after a redirect): render `shared/toast`,
// which leaves a `[data-sm-toast]` placeholder that is shown on load.

import { X } from 'lucide';

const VISIBLE_FOR = 4000;
// An error usually asks the reader to do something, so it stays a little longer.
const ERROR_VISIBLE_FOR = 8000;
const HOST_CLASS = 'sm-toast-host';
const SVG_NAMESPACE = 'http://www.w3.org/2000/svg';

// The host is a live region that exists before any message lands in it: screen
// readers tend to skip a status region that arrives with its text already inside.
function host() {
  let element = document.querySelector(`.${HOST_CLASS}`);
  if (!element) {
    element = document.createElement('div');
    element.className = HOST_CLASS;
    element.setAttribute('aria-live', 'polite');
    document.body.appendChild(element);
  }
  return element;
}

function buildIconNode([tag, attributes = {}, children = []]) {
  const element = document.createElementNS(SVG_NAMESPACE, tag);
  Object.entries(attributes).forEach(([name, value]) => element.setAttribute(name, value));
  children.forEach(child => element.append(buildIconNode(child)));
  return element;
}

function buildDismissButton() {
  const button = document.createElement('button');
  button.type = 'button';
  button.className = 'sm-toast__dismiss';
  button.setAttribute('aria-label', 'Dismiss');

  const icon = buildIconNode(X);
  icon.setAttribute('class', 'sm-toast__dismiss-icon');
  icon.setAttribute('aria-hidden', 'true');
  button.append(icon);
  return button;
}

export default function showToast(text, { variant } = {}) {
  // One at a time: a second message replaces the first rather than stacking,
  // because only the latest one is still true.
  const container = host();
  container.replaceChildren();

  const error = variant === 'error';
  const toast = document.createElement('div');
  toast.className = error ? 'sm-toast sm-toast--error' : 'sm-toast';
  // Errors interrupt; confirmations are read out by the host's polite region.
  if (error) toast.setAttribute('role', 'alert');

  const label = document.createElement('span');
  label.className = 'sm-toast__label';
  label.textContent = text;

  const dismiss = buildDismissButton();
  toast.append(label, dismiss);

  // Where focus goes back to if the toast is dismissed while it holds focus.
  let returnFocusTo = null;
  let timer = null;

  const remove = () => {
    clearTimeout(timer);
    const hadFocus = toast.contains(document.activeElement);
    toast.remove();
    if (hadFocus && returnFocusTo?.isConnected) returnFocusTo.focus();
  };
  const startTimer = () => {
    clearTimeout(timer);
    timer = setTimeout(remove, error ? ERROR_VISIBLE_FOR : VISIBLE_FOR);
  };

  dismiss.addEventListener('click', remove);
  // Never hide a message out from under someone reading it or about to dismiss it.
  toast.addEventListener('mouseenter', () => clearTimeout(timer));
  toast.addEventListener('mouseleave', () => { if (!toast.contains(document.activeElement)) startTimer(); });
  toast.addEventListener('focusin', event => {
    if (!toast.contains(event.relatedTarget)) returnFocusTo = event.relatedTarget;
    clearTimeout(timer);
  });
  toast.addEventListener('focusout', event => { if (!toast.contains(event.relatedTarget)) startTimer(); });

  container.appendChild(toast);
  // Commit the hidden starting state before rising into place, so the
  // transition always has something to run from.
  void toast.offsetHeight;
  toast.classList.add('sm-toast--in');
  startTimer();
  return toast;
}

document.addEventListener('DOMContentLoaded', () => {
  host();
  const placeholders = document.querySelectorAll('[data-sm-toast]');
  if (placeholders.length === 0) return;

  // A beat after the live region exists, so the message is announced.
  setTimeout(() => {
    placeholders.forEach(placeholder => {
      showToast(placeholder.dataset.smToast, { variant: placeholder.dataset.smToastVariant });
      placeholder.remove();
    });
  }, 100);
});
